import { useQuery, UseQueryOptions } from 'react-query';
import { APIError } from '~/src/utils/error';
import { useCurrentOrgFprint } from '~/src/entities/user/hooks';
import { composeQueryOpts, fetchAndParse } from '../../utils';
import { Banner } from '../types';
import { getBannersUrl } from '../urls';
import Cookies from 'js-cookie';

export const DISMISSED_BANNER_PREFIX = 'clio-draft-dismissed-banner-';
const DEFAULT_EXPIRY_DAYS = 7;

const defaultOptions: UseQueryOptions<Banner[], APIError> = {
  staleTime: Infinity,
  cacheTime: Infinity,
};

const getDismissedBannerKey = (bannerId: string) => {
  return `${DISMISSED_BANNER_PREFIX}${bannerId}`;
};

const getDismissedBannerIds = (): string[] => {
  return Object.keys(Cookies.get())
    .filter((key) => key.startsWith(DISMISSED_BANNER_PREFIX))
    .map((key) => key.replace(DISMISSED_BANNER_PREFIX, ''));
};

const removeDismissedBanner = (bannerId: string) => {
  Cookies.remove(getDismissedBannerKey(bannerId));
};

const dismissBanner = (banner: Banner) => {
  Cookies.set(`${DISMISSED_BANNER_PREFIX}${banner.id}`, 'true', {
    expires: banner.end_time ? new Date(banner.end_time) : DEFAULT_EXPIRY_DAYS,
  });
};

const filterBanners = (banners: Banner[]) => {
  const dismissedBannerIds = getDismissedBannerIds();

  // cleanup stale dismissed banners
  const cleanedUpDismissedBannerIds = dismissedBannerIds.reduce(
    (acc: string[], id: string) => {
      if (!banners.some((banner) => banner.id.toString() === id)) {
        removeDismissedBanner(id);
        return acc;
      }
      return [...acc, id];
    },
    [],
  );

  const activeBanners = banners.reduce((acc: Banner[], banner: Banner) => {
    if (cleanedUpDismissedBannerIds.includes(banner.id.toString())) {
      return acc;
    } else {
      return [...acc, banner];
    }
  }, []);

  return activeBanners;
};

export const useBanners = (
  options: UseQueryOptions<Banner[], APIError> = {},
) => {
  const orgFprint = useCurrentOrgFprint();
  const query = useQuery(
    composeQueryOpts(defaultOptions, options, {
      queryKey: ['banners'],
      queryFn: async () => {
        const banners = await fetchAndParse<Banner[]>(getBannersUrl(orgFprint));
        return filterBanners(banners);
      },
    }),
  );

  const handleDismissBanner = (banner: Banner) => {
    dismissBanner(banner);
    query.refetch();
  };

  return {
    ...query,
    dismissBanner: handleDismissBanner,
  };
};
